import React, {useRef, useEffect} from 'react';
import {Icon, Separator, DateContext, Scrollable, Layout, Activities, Flex, Card, Button, TimeTagPicker, UI, Breadcrumbs, NoWrap} from 'components';
import {StickyItems, TimeTooltip, TimeGrid, CalendarDay} from '.';
import {partialRight, isEmpty, moment, noop, size, config, map, modal, useGrabbed, useDrop, useDragStart, useNamedSelector} from 'helpers';
import {update, reschedule} from 'store/activities';
import {drop} from 'store/calendar';
import {forbid} from 'store/session';
import css from './routine-plan.module.scss';

export function RoutinePlan({ match: { params: { type } } }) {
  const focus = useRef();
  const { canAdd } = useNamedSelector('permissions.list');
  const date = useNamedSelector('calendar.routinePlanDate', { type });
  const add = canAdd.activities ? modal(Activities.Single.EditForm, { date }) : forbid;
  useEffect(() => {
    requestAnimationFrame(() => focus.current.scrollIntoView({ block: 'center' }));
  }, []);
  return (
    <main className={css('bleed', 'wrapper')}>
      <Layout.Header className={css('header')}>
        <div className={css('header-content')}>
          <Breadcrumbs>
            {date.format('dddd, MMM D')}
            <b>Plan {type === 'morning' ? 'Today' : 'Tomorrow'}</b>
          </Breadcrumbs>
          <Button icon="plus" className="small" onClick={add}>Activity</Button>
        </div>
        <div className="relative">
          <div className={css('labels')}>
            <div>Any time</div>
            <div>All day</div>
          </div>
          <div className={css('sticky-items')}>
            <DateContext.Provider value={date}>
              <StickyItems selector="calendar.anyTime" date={date} size={1} />
              <StickyItems selector="calendar.allDay" date={date} size={1} />
            </DateContext.Provider>
          </div>
        </div>
      </Layout.Header>
      <div className={css.section}>
        <TimeGrid className={css.timegrid} />
        <div className={css.focus} aria-current="time" ref={focus} />
        <div className={css.canvas} id="calendar-timetable">
          <ul className={css.columns}>
            <CalendarDay date={date} card={{ hasCheckbox: false }} />
          </ul>
        </div>
        <TimeTooltip />
      </div>
    </main>
  );
}

export function RoutinePlanSidebar({ match: { params: { type } } }) {
  const {today, tomorrow} = useNamedSelector('calendar.days');
  const grabbed = useGrabbed();
  const { canAdd } = useNamedSelector('permissions.list');
  const date = useNamedSelector('calendar.routinePlanDate', { type });
  const add = canAdd.activities ? modal(Activities.Single.EditForm, { date }) : forbid;
  const previousDate = useNamedSelector('calendar.routinePlanPreviousDate', { type });
  const missed = useNamedSelector('calendar.routinePlanMissed', { date: previousDate });
  const scheduled = useNamedSelector('calendar.routinePlanScheduled', { date });
  const { groups } = useNamedSelector('calendar.routinePlan', { type, missed, scheduled });
  return (
    <Layout.Sidebar>
      { type === 'morning'
        ? <h5>Which tasks are most critical to do today?</h5>
        : <h5>What are the most critical activities for tomorrow?</h5>
      }
      { isEmpty(groups)
        ? <p>You have no activities.</p>
        : <p>Review the list below, <NoWrap><Icon name="hold" /> grab</NoWrap> activities that you should complete today, and drop them into your schedule (on the right).</p>
      }
      <Layout.Sidebar.Scrollable>
        <DateContext.Provider value={previousDate}>
          { map(groups, (group, title) => (
            <div className="medium-spacing" key={title}>
              <Scrollable.Header>
                <Separator className="medium" heading={title} />
              </Scrollable.Header>
              { group.map(activity =>
                <Activities.Card
                  Checkbox={Activities.Checkbox}
                  detailed={false}
                  id={activity.id}
                  key={activity.id}
                  onClick={noop}
                  {...useDragStart({
                    contextDate: activity.repeatType ? previousDate : moment.unix(activity.ts),
                    id: activity.id,
                    occurance: activity.occurance,
                    type: 'activity'
                  })}
                />
              ) }
            </div>
          ))}
        </DateContext.Provider>
      </Layout.Sidebar.Scrollable>
      {grabbed?.type === 'calendar-activity' &&
        <div className={css.dropzones}>
          {type === 'morning' &&
            <div {...useDrop(partialRight(drop, tomorrow, {}))}>
              Due Tomorrow
              <small>{tomorrow.format('dddd, MMM D')}</small>
            </div>
          }
          {!grabbed?.repeatType &&
            <div {...useDrop(partialRight(drop, null, {}))}>
              Due Someday
            </div>
          }
        </div>
      }
    </Layout.Sidebar>
  );
}
